import {CommentsCategory, useAddComment, useEditComment, type CommentDto} from '@domain/comments'
import {dataTestId} from '@hconnect/uikit'
import {HPTextField} from '@hconnect/uikit/src/lib2'
import {useUpmPlantContext} from '@hconnect/upm'
import {spawnErrorToast, spawnSuccessToast} from '@hm/fundamental'
import React, {useMemo} from 'react'
import {useErrorHandler} from 'react-error-boundary'
import {useForm, Controller} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {useAutoSaveValue} from '../../hooks'
import {notifyIfErrorMessage} from '../../utils'

interface MaterialOrderCommentFormProps {
  isDisabled: boolean
  materialOrderId?: number
  comment?: CommentDto
}

export const MaterialOrderCommentForm: React.FC<MaterialOrderCommentFormProps> = ({
  materialOrderId,
  isDisabled,
  comment
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const raiseError = useErrorHandler()
  const {plantId: plantCode} = useUpmPlantContext()
  const {mutate: addComment} = useAddComment(
    {
      plantId: plantCode,
      commentsCategory: CommentsCategory.MaterialOrders
    },
    'comments',
    {mutationKey: ['addMaterialOrderComment']}
  )
  const {mutate: editComment} = useEditComment(
    {
      plantId: plantCode,
      commentsCategory: CommentsCategory.MaterialOrders
    },
    'comments',
    {
      mutationKey: ['editMaterialOrderComment'],
      onError: (error) => {
        // Type assertion to expected error response shape
        const message = (error.response?.data as {message?: string})?.message
        notifyIfErrorMessage(message, raiseError, spawnErrorToast)
      },
      onSuccess: () => {
        spawnSuccessToast(t('success.editMaterialOrderComment'))
      }
    }
  )

  const {
    handleSubmit,
    control,
    reset,
    watch,
    formState: {isDirty}
  } = useForm({
    defaultValues: {commentValue: comment?.value ?? ''}
  })

  const commentId = comment?.commentId

  const onSubmit = useMemo(
    () =>
      handleSubmit(({commentValue}) => {
        if (!materialOrderId) {
          return
        }
        if (commentId) {
          return editComment(
            {
              value: commentValue,
              id: commentId,
              commentCategoryInstanceId: String(materialOrderId)
            },
            {onSuccess: () => reset({commentValue}), onError: () => reset()}
          )
        }
        return addComment(
          {value: commentValue, commentCategoryInstanceId: String(materialOrderId)},
          {onSuccess: () => reset({commentValue}), onError: () => reset()}
        )
      }),
    [editComment, addComment, commentId, reset, handleSubmit, materialOrderId]
  )
  const currentCommentValue = watch('commentValue')

  useAutoSaveValue({
    currentValue: currentCommentValue,
    shouldSave: isDirty && currentCommentValue !== '',
    saveFn: onSubmit
  })

  return (
    <Controller
      name="commentValue"
      control={control}
      render={({field: {value, onChange}, fieldState: {error}}) => (
        <HPTextField
          multiline
          minRows={3}
          label={t('common.comment')}
          value={value}
          onChange={onChange}
          error={Boolean(error?.message)}
          helperText={error?.message}
          disabled={isDisabled}
          {...dataTestId('material_order_comment_input')}
        />
      )}
    />
  )
}
